/**
 * 返信管理 API hooks (Phase 3 第2層).
 *
 * GET/PATCH/POST /api/emails/replies/*
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

export type ReplyResponseStatus = "unresponded" | "in_progress" | "completed";
export type ReplyParentType = "delivery" | "sent_email";
export type ReplyLinkMethod = "header" | "references" | "subject" | "manual";
export type ReplyAxis = "delivery" | "thread" | "sent";

export const REPLY_STATUSES: readonly ReplyResponseStatus[] = [
  "unresponded",
  "in_progress",
  "completed",
] as const;

/**
 * 返信対応ステータスの日本語ラベル
 * @param {ReplyResponseStatus} status ステータス
 * @return {string} 表示ラベル
 */
export function replyStatusLabel(status: ReplyResponseStatus): string {
  switch (status) {
    case "unresponded":
      return "未対応";
    case "in_progress":
      return "対応中";
    case "completed":
      return "完了";
  }
}

/**
 * 返信対応ステータスに対応する左リストのドット色クラス
 * @param {ReplyResponseStatus} status ステータス
 * @return {string} Tailwind bg-* クラス
 */
export function replyStatusDotClass(status: ReplyResponseStatus): string {
  switch (status) {
    case "unresponded":
      return "bg-rose-500";
    case "in_progress":
      return "bg-amber-400";
    case "completed":
      return "bg-emerald-500";
  }
}

/**
 * 返信対応ステータスに対応する Badge color (LEAF-0002 Badge と整合)
 * @param {ReplyResponseStatus} status ステータス
 * @return {"error"|"warning"|"success"} Badge color
 */
export function replyStatusBadgeColor(
  status: ReplyResponseStatus,
): "error" | "warning" | "success" {
  switch (status) {
    case "unresponded":
      return "error";
    case "in_progress":
      return "warning";
    case "completed":
      return "success";
  }
}

export type ReplyEmailItem = {
  id: string;
  parentType: ReplyParentType;
  deliveryEmailId: string | null;
  deliveryRecipientId: string | null;
  sentEmailId: string | null;
  salesPipelineId: string | null;
  focusMatterId: string | null;
  emailConnectionId: string;
  providerMessageId: string;
  providerThreadId: string | null;
  messageId: string | null;
  inReplyTo: string | null;
  fromEmail: string;
  fromName: string | null;
  subject: string;
  bodyText: string | null;
  bodyHtml: string | null;
  responseStatus: ReplyResponseStatus;
  linkMethod: ReplyLinkMethod | null;
  receivedAt: string;
  respondedBy: string | null;
  respondedByName: string | null;
  respondedAt: string | null;
  responseNote: string | null;
  companyName: string | null;
  deliverySubject: string | null;
  /** 紐付く配信シリーズ ID (delivery 経由でのみ設定される) */
  seriesId: string | null;
  /** 紐付く配信シリーズ名 */
  seriesName: string | null;
  sentEmailSubject: string | null;
  pipelineTalentName: string | null;
  pipelineMatterTitle: string | null;
};

type ReplyListResponse = {
  items: ReplyEmailItem[];
  total: number;
  page: number;
  limit: number;
};

export type ReplyStats = {
  total: number;
  unresponded: number;
  inProgress: number;
  completed: number;
  responseRate: number;
};

export type ReplyGroupSummary = {
  groupId: string;
  groupLabel: string;
  total: number;
  unresponded: number;
  lastReceivedAt: string | null;
};

type ReplyGroupListResponse = {
  items: ReplyGroupSummary[];
};

export type ReplyListFilters = {
  axis?: ReplyAxis;
  groupId?: string | null;
  /** 配信シリーズで絞り込み (任意) */
  seriesId?: string | null;
  status?: ReplyResponseStatus | null;
  page?: number;
  limit?: number;
};

/**
 * 返信一覧を取得する hook
 * @param {ReplyListFilters} filters フィルタ
 * @return {object} items, total, loading, error, refetch
 */
export function useReplies(filters: ReplyListFilters): {
  items: ReplyEmailItem[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [items, setItems] = useState<ReplyEmailItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const { axis, groupId, seriesId, status, page, limit } = filters;

  const fetchReplies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (axis) params.axis = axis;
      if (groupId) params.groupId = groupId;
      if (seriesId) params.seriesId = seriesId;
      if (status) params.status = status;
      if (page) params.page = String(page);
      if (limit) params.limit = String(limit);
      const response = await restClient.get<ReplyListResponse>(
        "/emails/replies",
        params,
      );
      setItems(response.items);
      setTotal(response.total);
    } catch (e) {
      // エラー時は items を空にする (古い成功時データを残すと、 別フィルタで取得した
      // 結果が現在のフィルタの結果として誤表示される silent fail を起こす)
      setItems([]);
      setTotal(0);
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, [axis, groupId, seriesId, status, page, limit]);

  useEffect(() => {
    fetchReplies();
  }, [fetchReplies]);

  return { items, total, loading, error, refetch: fetchReplies };
}

/**
 * 返信ステータス集計を取得する hook
 * @return {object} stats, loading, refetch
 */
export function useReplyStats(): {
  stats: ReplyStats | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [stats, setStats] = useState<ReplyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.get<ReplyStats>(
        "/emails/replies/stats",
      );
      setStats(response);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
}

/**
 * 返信ステータス更新 hook
 * @return {object} update, loading, error
 */
export function useUpdateReplyStatus(): {
  updateStatus: (
    replyId: string,
    status: ReplyResponseStatus,
  ) => Promise<ReplyEmailItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const updateStatus = useCallback(
    async (replyId: string, status: ReplyResponseStatus) => {
      setLoading(true);
      setError(null);
      try {
        const response = await restClient.patch<ReplyEmailItem>(
          `/emails/replies/${replyId}/status`,
          { responseStatus: status },
        );
        return response;
      } catch (e) {
        const err = toApiError(e);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateStatus, loading, error };
}

/**
 * 返信メモ更新 hook
 * @return {object} updateNote, loading, error
 */
export function useUpdateReplyNote(): {
  updateNote: (replyId: string, note: string | null) => Promise<ReplyEmailItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const updateNote = useCallback(
    async (replyId: string, note: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const response = await restClient.patch<ReplyEmailItem>(
          `/emails/replies/${replyId}/note`,
          { responseNote: note },
        );
        return response;
      } catch (e) {
        const err = toApiError(e);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateNote, loading, error };
}

/**
 * パイプライン紐付け hook
 * @return {object} linkPipeline, loading, error
 */
export function useLinkReplyPipeline(): {
  linkPipeline: (
    replyId: string,
    salesPipelineId: string | null,
  ) => Promise<ReplyEmailItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const linkPipeline = useCallback(
    async (replyId: string, salesPipelineId: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const response = await restClient.patch<ReplyEmailItem>(
          `/emails/replies/${replyId}/link-pipeline`,
          { salesPipelineId },
        );
        return response;
      } catch (e) {
        const err = toApiError(e);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { linkPipeline, loading, error };
}

export type BatchAction =
  | "mark_completed"
  | "mark_in_progress"
  | "mark_unresponded";

/**
 * 一括操作 hook
 * @return {object} batch, loading, error
 */
export function useReplyBatch(): {
  batch: (replyIds: string[], action: BatchAction) => Promise<number>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const batch = useCallback(async (replyIds: string[], action: BatchAction) => {
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.post<{ updatedCount: number }>(
        "/emails/replies/batch",
        { replyIds, action },
      );
      return response.updatedCount;
    } catch (e) {
      const err = toApiError(e);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { batch, loading, error };
}

/**
 * 任意の error を RestApiError に統一する
 * @param {unknown} e 発生した error
 * @return {RestApiError} ラップされた error
 */
function toApiError(e: unknown): RestApiError {
  if (e instanceof RestApiError) return e;
  return new RestApiError(
    e instanceof Error ? e.message : "Unknown error",
    500,
  );
}
